/**
 * Sleep for a specified duration.
 *
 * @param {number} ms - Duration in milliseconds
 * @returns {Promise} - Resolves after the duration
 */
const sleep = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Internal retry implementation using recursion to avoid await-in-loop.
 *
 * @param {Function} operation - Async function to execute
 * @param {number} attempt - Current attempt number (0-indexed)
 * @param {number} maxRetries - Maximum number of retry attempts
 * @param {number} baseDelayMs - Base delay in milliseconds
 * @param {number} maxDelayMs - Maximum delay in milliseconds
 * @returns {Promise} - Result of the operation
 */
const executeWithRetry = async (
  operation,
  attempt,
  maxRetries,
  baseDelayMs,
  maxDelayMs,
) => {
  try {
    return await operation();
  } catch (error) {
    // Don't retry on non-retryable errors
    if (!isRetryableError(error)) {
      throw error;
    }

    // Don't retry if we've exhausted attempts
    if (attempt >= maxRetries) {
      throw error;
    }

    // Calculate delay with exponential backoff and jitter
    const delay = calculateDelay(attempt, baseDelayMs, maxDelayMs);

    await sleep(delay);

    // Recursive call for next attempt
    return executeWithRetry(
      operation,
      attempt + 1,
      maxRetries,
      baseDelayMs,
      maxDelayMs,
    );
  }
};

/**
 * Retry utility with exponential backoff for GraphQL operations.
 *
 * @param {Function} operation - Async function to execute
 * @param {Object} config - Configuration options
 * @param {number} [config.maxRetries=3] - Maximum number of retry attempts
 * @param {number} [config.baseDelayMs=1000] - Base delay in milliseconds
 * @param {number} [config.maxDelayMs=10000] - Maximum delay in milliseconds
 * @returns {Promise} - Result of the operation
 */
export const withRetry = async (operation, config = {}) => {
  const { maxRetries = 3, baseDelayMs = 1000, maxDelayMs = 10000 } = config;

  return executeWithRetry(operation, 0, maxRetries, baseDelayMs, maxDelayMs);
};

/**
 * Determines if an error is retryable (network-related).
 *
 * @param {Error} error - The error to check
 * @returns {boolean} - Whether the error is retryable
 */
export const isRetryableError = (error) => {
  // Network errors
  if (error.message?.includes("Network Error")) {
    return true;
  }
  if (error.message?.includes("Failed to fetch")) {
    return true;
  }
  if (error.message?.includes("NetworkError")) {
    return true;
  }
  if (error.code === "ECONNRESET") {
    return true;
  }
  if (error.code === "ETIMEDOUT") {
    return true;
  }

  // Amplify/AppSync specific errors
  if (error.errors?.some?.((e) => e.errorType === "ServiceUnavailable")) {
    return true;
  }
  if (error.errors?.some?.((e) => e.errorType === "InternalServerError")) {
    return true;
  }

  // HTTP status codes that indicate transient errors
  const status = error.status || error.statusCode;
  if (status === 429 || status === 502 || status === 503 || status === 504) {
    return true;
  }

  return false;
};

/**
 * Calculates delay with exponential backoff and jitter.
 *
 * @param {number} attempt - Current attempt number (0-indexed)
 * @param {number} baseDelayMs - Base delay in milliseconds
 * @param {number} maxDelayMs - Maximum delay in milliseconds
 * @returns {number} - Delay in milliseconds
 */
export const calculateDelay = (attempt, baseDelayMs, maxDelayMs) => {
  // Exponential backoff: baseDelay * 2^attempt
  const exponentialDelay = baseDelayMs * 2 ** attempt;

  // Cap at maxDelayMs
  const cappedDelay = Math.min(exponentialDelay, maxDelayMs);

  // Add jitter (0-50% of delay) to prevent thundering herd
  const jitter = Math.random() * cappedDelay * 0.5;

  return cappedDelay + jitter;
};

export default withRetry;
